import { assertNever } from '../utils/typeHelper';

export type CurrentShowSpecialUrlCode =
  | SpecialUrlCode.Profile
  | SpecialUrlCode.LiveShow
  | SpecialUrlCode.UpcomingShow
  | SpecialUrlCode.Replay
  | SpecialUrlCode.Event
  | SpecialUrlCode.Promotion;

export type SpecialUrlCode =
  | SpecialUrlCode.Profile
  | SpecialUrlCode.LiveShow
  | SpecialUrlCode.UpcomingShow
  | SpecialUrlCode.Event
  | SpecialUrlCode.Promotion
  | SpecialUrlCode.Review;

export namespace SpecialUrlCode {
  export type Profile = `u_${string}`;

  export const urlCodeWithReelOnly = (urlCode: string) => `${urlCode}?reel`;

  export const isUrlCodeWithReelOnly = (urlCode: string) =>
    urlCode.endsWith('?reel');

  export const urlCodeWithReelOnlyRemoved = (urlCode: string) =>
    urlCode.replace('?reel', '');

  const withPrefix = (s: string, prefix: string, caseInsensitive = false) =>
    caseInsensitive
      ? s.toLowerCase().startsWith(prefix.toLowerCase())
      : s.startsWith(prefix);

  export function isProfile(
    code: string,
    caseInsensitive = false,
  ): code is Profile {
    return withPrefix(code, 'u_', caseInsensitive);
  }
  export function profile(profileId: string): Profile {
    return `u_${profileId}`;
  }

  export type LiveShow = `liveShow_${string}`;
  export function isLiveShow(
    code: string,
    caseInsensitive = false,
  ): code is LiveShow {
    return withPrefix(code, 'liveShow_', caseInsensitive);
  }
  export function liveShow(liveShowId: string): LiveShow {
    return `liveShow_${liveShowId}`;
  }

  export type Replay = `replay_${string}`;
  export function isReplay(
    code: string,
    caseInsensitive = false,
  ): code is Replay {
    return withPrefix(code, 'replay_', caseInsensitive);
  }
  export function replay(liveShowId: string): Replay {
    return `replay_${liveShowId}`;
  }

  export type UpcomingShow = `upcomingShow_${string}`;
  export function isUpcomingShow(
    code: string,
    caseInsensitive = false,
  ): code is UpcomingShow {
    return withPrefix(code, 'upcomingShow_', caseInsensitive);
  }
  export function upcomingShow(upcomingShowId: string): UpcomingShow {
    return `upcomingShow_${upcomingShowId}`;
  }

  export type Event = `event_${string}`;
  export function isEvent(
    code: string,
    caseInsensitive = false,
  ): code is Event {
    return withPrefix(code, 'event_', caseInsensitive);
  }
  export function event(eventId: string): Event {
    return `event_${eventId}`;
  }

  export type Promotion = `promotion_${string}`;
  export function isPromotion(
    code: string,
    caseInsensitive = false,
  ): code is Promotion {
    return withPrefix(code, 'promotion_', caseInsensitive);
  }
  export function promotion(promotionId: string): Promotion {
    return `promotion_${promotionId}`;
  }

  export type Review = `review_${string}`;
  export function isReview(
    code: string,
    caseInsensitive = false,
  ): code is Review {
    return withPrefix(code, 'review_', caseInsensitive);
  }
  export function review(reviewId: string): Review {
    return `review_${reviewId}`;
  }

  export type PopClip_v1 = `clip_v1_${string}_${string}`;
  export function isPopClipV1(
    code: string,
    caseInsensitive = false,
  ): code is PopClip_v1 {
    return withPrefix(code, 'clip_v1_', caseInsensitive);
  }
  export function popclipV1(sellerId: string, clipId: string): PopClip_v1 {
    return `clip_v1_${sellerId}_${clipId}`;
  }

  export function isMatch(str: string): str is SpecialUrlCode {
    const _str: any = str;
    return (
      isProfile(_str) ||
      isLiveShow(_str) ||
      isUpcomingShow(_str) ||
      isEvent(_str) ||
      isPromotion(_str) ||
      isReview(_str) ||
      isPopClipV1(_str)
    );
  }

  export function isCurrentShowSpecialUrlCode(
    str: string,
  ): str is CurrentShowSpecialUrlCode {
    const _str: any = str;
    return (
      isProfile(_str) ||
      isLiveShow(_str) ||
      isUpcomingShow(_str) ||
      isReplay(_str) ||
      isEvent(_str) ||
      isPromotion(_str)
    );
  }
}

export type DecodedCurrentShowSpecialUrlCode =
  | DecodedSpecialUrlCode.Profile
  | DecodedSpecialUrlCode.LiveShow
  | DecodedSpecialUrlCode.UpcomingShow
  | DecodedSpecialUrlCode.Event
  | DecodedSpecialUrlCode.Promotion
  | DecodedSpecialUrlCode.Replay;

export type DecodedSpecialUrlCode =
  | DecodedSpecialUrlCode.Profile
  | DecodedSpecialUrlCode.LiveShow
  | DecodedSpecialUrlCode.UpcomingShow
  | DecodedSpecialUrlCode.Event
  | DecodedSpecialUrlCode.Promotion
  | DecodedSpecialUrlCode.Review
  | DecodedSpecialUrlCode.Replay
  | DecodedSpecialUrlCode.PopClip_v1;

export namespace DecodedSpecialUrlCode {
  export interface Profile {
    type: 'profile';
    profileId: string;
  }
  export function decodeProfile(code: SpecialUrlCode.Profile): Profile {
    return { type: 'profile', profileId: code.replace('u_', '') };
  }

  export interface LiveShow {
    type: 'liveShow';
    liveShowId: string;
  }
  export function decodeLiveShow(code: SpecialUrlCode.LiveShow): LiveShow {
    return { type: 'liveShow', liveShowId: code.replace('liveShow_', '') };
  }

  export interface Replay {
    type: 'replay';
    liveShowId: string;
  }
  export function decodeReplay(code: SpecialUrlCode.Replay): Replay {
    return { type: 'replay', liveShowId: code.replace('replay_', '') };
  }

  export interface UpcomingShow {
    type: 'upcomingShow';
    upcomingShowId: string;
  }
  export function decodeUpcomingShow(
    code: SpecialUrlCode.UpcomingShow,
  ): UpcomingShow {
    return {
      type: 'upcomingShow',
      upcomingShowId: code.replace('upcomingShow_', ''),
    };
  }

  export interface Event {
    type: 'event';
    eventId: string;
  }
  export function decodeEvent(code: SpecialUrlCode.Event): Event {
    return { type: 'event', eventId: code.replace('event_', '') };
  }

  export interface Promotion {
    type: 'promotion';
    promotionId: string;
  }
  export function decodePromotion(code: SpecialUrlCode.Promotion): Promotion {
    return { type: 'promotion', promotionId: code.replace('promotion_', '') };
  }

  export interface Review {
    type: 'review';
    reviewId: string;
  }
  export function decodeReview(code: SpecialUrlCode.Review): Review {
    return { type: 'review', reviewId: code.replace('review_', '') };
  }

  export interface PopClip_v1 {
    type: 'popclip_v1';
    clipId: string;
    sellerId: string;
  }
  export function decodePopClipV1(code: SpecialUrlCode.PopClip_v1): PopClip_v1 {
    const [sellerId, clipId] = code.replace('clip_v1_', '').split('_');
    return { type: 'popclip_v1', sellerId, clipId };
  }

  export function decode(code: SpecialUrlCode): DecodedSpecialUrlCode {
    // prettier-ignore
    return (
      SpecialUrlCode.isProfile(code) ? DecodedSpecialUrlCode.decodeProfile(code) :
      SpecialUrlCode.isLiveShow(code) ? DecodedSpecialUrlCode.decodeLiveShow(code) :
      SpecialUrlCode.isUpcomingShow(code) ? DecodedSpecialUrlCode.decodeUpcomingShow(code) :
      SpecialUrlCode.isEvent(code) ? DecodedSpecialUrlCode.decodeEvent(code) :
      SpecialUrlCode.isPromotion(code) ? DecodedSpecialUrlCode.decodePromotion(code) :
      SpecialUrlCode.isReview(code) ? DecodedSpecialUrlCode.decodeReview(code) :
      SpecialUrlCode.isReplay(code) ? DecodedSpecialUrlCode.decodeReplay(code) :
      SpecialUrlCode.isPopClipV1(code) ? DecodedSpecialUrlCode.decodePopClipV1(code) :
      assertNever(code)
    );
  }

  export function decodeCurrentShowSpecialUrlCode(
    code: CurrentShowSpecialUrlCode,
  ): DecodedCurrentShowSpecialUrlCode {
    // prettier-ignore
    return (
      SpecialUrlCode.isProfile(code) ? DecodedSpecialUrlCode.decodeProfile(code) :
      SpecialUrlCode.isLiveShow(code) ? DecodedSpecialUrlCode.decodeLiveShow(code) :
      SpecialUrlCode.isUpcomingShow(code) ? DecodedSpecialUrlCode.decodeUpcomingShow(code) :
      SpecialUrlCode.isEvent(code) ? DecodedSpecialUrlCode.decodeEvent(code) :
      SpecialUrlCode.isPromotion(code) ? DecodedSpecialUrlCode.decodePromotion(code) :
      SpecialUrlCode.isReplay(code) ? DecodedSpecialUrlCode.decodeReplay(code) :
      assertNever(code)
    );
  }
}
